import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Code, Bot, Sparkles, ChevronRight, MapPin } from "lucide-react";

const HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_learn-code-kids-3/artifacts/z6o7y9jb_Image%2011%20%D0%BC%D0%B0%D1%80.%202026%20%D0%B3.%2C%2001_04_52.png",
];

export const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[90vh] flex items-center hero-gradient noise-bg overflow-hidden pt-[75px] md:pt-20"
    >
      {/* Floating background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ y: [-8, 8, -8] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[15%] left-[8%] w-16 h-16 rounded-2xl bg-[#F28C38]/15 rotate-12"
        />
        <motion.div
          animate={{ y: [6, -6, 6] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[25%] right-[12%] w-12 h-12 rounded-full bg-[#00A676]/12"
        />
        <motion.div
          animate={{ y: [-5, 5, -5], rotate: [0, 5, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-[20%] left-[15%] w-10 h-10 rounded-lg bg-[#D52B1E]/10 rotate-45"
        />
        <motion.div
          animate={{ y: [4, -8, 4] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-[30%] right-[8%] w-14 h-14 rounded-2xl bg-[#F7D154]/15 -rotate-12"
        />
        {/* Code symbols */}
        <motion.span
          animate={{ y: [-6, 6, -6], opacity: [0.12, 0.2, 0.12] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[18%] right-[25%] text-4xl font-mono text-[#1677FF]/15 font-bold select-none"
        >
          &lt;/&gt;
        </motion.span>
        <motion.span
          animate={{ y: [5, -7, 5], opacity: [0.1, 0.18, 0.1] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-[25%] left-[5%] text-3xl font-mono text-[#F28C38]/15 font-bold select-none"
        >
          {"{ }"}
        </motion.span>
      </div>

      <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6 py-12 md:py-0">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold bg-white border border-border shadow-sm mb-6"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#F7D154]" />
              <span className="text-foreground/70">Online Academy</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#00A676]"></span>
              <span className="text-foreground/70">Ages 6–14</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6"
            >
              Learn{" "}
              <span className="text-[#1677FF]">Coding</span>,{" "}
              <span className="text-[#F28C38]">Robotics</span>{" "}
              &{" "}
              <span className="text-[#00A676]">Digital Creativity</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0"
            >
              We help kids explore coding, robotics, and digital creativity through fun, hands-on learning.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start"
            >
              <Link to="/contact">
                <Button
                  data-testid="hero-primary-cta-button"
                  size="lg"
                  className="rounded-full bg-[#1677FF] hover:bg-[#0F63DA] text-white shadow-[var(--btn-shadow)] btn-lift px-8 h-12 text-base font-semibold"
                >
                  Book a Free Trial Class
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/programs">
                <Button
                  data-testid="hero-secondary-cta-button"
                  variant="outline"
                  size="lg"
                  className="rounded-full border-border bg-white hover:bg-secondary px-8 h-12 text-base font-semibold btn-lift"
                >
                  Explore Programs
                </Button>
              </Link>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="flex flex-wrap items-center gap-3 mt-8 justify-center lg:justify-start"
            >
              {["Hands-on Learning", "Small Groups", "Expert Instructors"].map((text) => (
                <span key={text} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-white border border-border sticker-shadow">
                  <Sparkles className="w-3 h-3 text-[#F7D154]" />
                  {text}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Image Collage */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Main image */}
              <motion.div
                animate={{ y: [-4, 4, -4] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-4 border-white"
              >
                <img
                  src={HERO_IMAGES[0]}
                  alt="Kids learning coding and robotics at Maple Code Academy"
                  className="w-full h-[400px] object-cover"
                  loading="eager"
                />
              </motion.div>

              {/* Floating sticker badges */}
              <motion.div
                animate={{ y: [-6, 6, -6], rotate: [-3, 3, -3] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -right-4 z-30 bg-white rounded-2xl px-4 py-3 shadow-lg border border-border"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#1677FF]/10 flex items-center justify-center">
                    <Code className="w-4 h-4 text-[#1677FF]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold">100+</p>
                    <p className="text-[10px] text-muted-foreground">Projects Built</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [5, -7, 5], rotate: [2, -2, 2] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-4 -right-6 z-30 bg-white rounded-2xl px-4 py-3 shadow-lg border border-border"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#00A676]/10 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-[#00A676]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold">Robotics</p>
                    <p className="text-[10px] text-muted-foreground">STEM Projects</p>
                  </div>
                </div>
              </motion.div>

              {/* Background accent blob */}
              <div className="absolute -top-6 -right-6 w-[120%] h-[120%] rounded-3xl bg-[#1677FF]/5 -z-10 rotate-3"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
